import { useState } from "react";

import {
  LeaderboardPage,
  LeaderboardSortingOption,
} from "lib/types/leaderboards";
import SortButton from "./sortButton/sortButton";
import Row from "./row/row";

import { sortTable } from "./utils";
import { downloadCSV } from "./downloadCSV";
import { ClientOnlyPaywall } from "components/paywallModal/clientOnlyPaywall";
import { PaywallWindow } from "lib/types/paywalls";
import SimpleButton from "components/simpleButton/simpleButton";
import { withoutRevenueFields } from "content-ui/leaderboards/lib";

type TableProps = LeaderboardPage & {
  onTrackMixpanel: (
    event: string,
    data: {
      [key: string]: string;
    }
  ) => Promise<void>;
  isProUser: boolean;
  downloadPaywall?: PaywallWindow | null;
};

const SORT_MAP = {
  rank: "Rank",
  revenueCagr: "Revenue growth",
  latestRevenue: "Latest revenue",
  employees: "Employees",
  totalFunding: "Total funding",
};

type SortType = {
  key: LeaderboardSortingOption;
  value: "ASC" | "DESC";
};

const Table = ({
  sorting,
  list,
  columns,
  isProUser,
  onTrackMixpanel,
  downloadPaywall = null,
}: TableProps) => {
  const [sort, setSort] = useState<SortType>({ key: "rank", value: "DESC" });
  const [data, setData] = useState<LeaderboardPage["list"]>(
    sortTable(list, sort)
  );
  const [showDownloadPaywall, setShowDownloadPaywall] = useState(false);

  const visibleSorting = isProUser ? sorting : withoutRevenueFields(sorting);

  const handleSortChange = ({ key }: Pick<SortType, "key">) => {
    // Sort order => DESC -> ASC

    if (key === sort.key) {
      if (sort.value === "ASC") {
        setData(sortTable(list, { key: key, value: "DESC" }));
        setSort({ key: key, value: "DESC" });
      }
      if (sort.value === "DESC") {
        setData(sortTable(list, { key: key, value: "ASC" }));
        setSort({ key: key, value: "ASC" });
      }
    } else {
      setData(sortTable(list, { key: key, value: "DESC" }));
      setSort({ key: key, value: "DESC" });
    }
  };

  const handleDownloadCSV = () => {
    onTrackMixpanel("Leaderboard download", {
      table: document.title,
    });

    if (isProUser) {
      downloadCSV(data);
      return;
    }

    if (!isProUser && downloadPaywall) {
      setShowDownloadPaywall(true);
    }
  };

  return (
    <>
      {showDownloadPaywall && downloadPaywall && (
        <ClientOnlyPaywall paywall={downloadPaywall} />
      )}
      <div className={"flex flex-col"}>
        {visibleSorting.length > 0 && (
          <div className={"flex flex-col"}>
            <h2 className={"mb-[12px] font-bold text-[16px] leading-[130%]"}>
              Sort by
            </h2>
            <div className={"flex flex-row justify-between"}>
              <ul
                aria-label={"List of sort options"}
                className={"flex gap-[26px] overflow-y-auto"}
              >
                {visibleSorting.map((sortKey) => {
                  return (
                    <li
                      key={sortKey}
                      className={
                        "relative after:absolute after:right-[-12px] after:top-[16px] after:h-[18px] after:w-[2px] after:bg-mono-20 after:content-[''] last:after:content-none"
                      }
                    >
                      <SortButton
                        label={SORT_MAP[sortKey]}
                        columnKey={sortKey}
                        sort={sort.key === sortKey ? sort.value : null}
                        onClick={() => handleSortChange({ key: sortKey })}
                      />
                    </li>
                  );
                })}
              </ul>
              <div className={"flex flex-col gap-[6px]"}>
                <SimpleButton
                  size="md"
                  variant="primary"
                  accent="pro"
                  onClick={handleDownloadCSV}
                  data-testid="download-csv"
                >
                  Download Data
                </SimpleButton>
                {!isProUser && (
                  <p className={"text-mono-60 text-[12px]"}>
                    Available to Sifted Pro subscribers
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        <div className={"mt-[22px]"}>
          <ul className={"flex flex-col gap-[10px]"}>
            {data.map((company) => {
              return (
                <li key={company.id} className={"w-full"}>
                  <Row
                    company={company}
                    columns={columns}
                    onTrackMixpanel={onTrackMixpanel}
                    isProUser={isProUser}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
};

export default Table;
